import { useEffect, useRef, useState } from "react";
import { useContent } from "../../context/ContentContext";

const FALLBACK = {
  ledgerTag: "The Ledger",
  footnote: "Every application is read. Not every application is answered.",
  stats: [
    { num: "0", label: "Applicants", tag: "Submitted", source: "applicants_count", live: true },
    { num: "118", label: "Cities", tag: "Mapped" },
    { num: "212", label: "Accepted", tag: "Chosen" },
  ],
};

const ROMANS = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

// Parse a number string like "4,217" → 4217. Returns null for non-numeric (e.g., "12%").
const parseNumeric = (s) => {
  const digits = String(s ?? "").replace(/[, ]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(digits)) return null;
  return Number(digits);
};

const formatNumber = (n, original) => {
  // Preserve the comma style of the original (e.g., "4,217" → grouped)
  const hadComma = /,/.test(String(original));
  if (Number.isInteger(n)) {
    return hadComma ? n.toLocaleString("en-US") : String(n);
  }
  return hadComma ? n.toLocaleString("en-US") : String(n);
};

// Count-up hook
const useCountUp = (target, run, duration = 1400) => {
  const [value, setValue] = useState(0);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!run || target == null) return;
    const start = performance.now();
    startRef.current = start;
    const from = 0;
    const animate = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + (target - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, run, duration]);

  return value;
};

const StatBlock = ({ stat, index, inView }) => {
  const numeric = parseNumeric(stat.num);
  const live = stat.live === true || stat.source === "applicants_count";
  const target = numeric ?? 0;
  const animated = useCountUp(target, inView && numeric !== null);
  const displayed =
    numeric === null
      ? stat.num
      : formatNumber(Math.round(animated), stat.num);

  return (
    <div
      data-testid={`counter-stat-${index}`}
      className="group relative pl-6 md:pl-8"
    >
      {/* Vertical gold rule + small accent block */}
      <span className="absolute left-0 top-2 bottom-2 w-px bg-gold/35 group-hover:bg-gold/70 transition-colors duration-500" />
      <span className="absolute left-[-2px] top-2 w-[5px] h-[5px] bg-gold rotate-45" />

      {/* Top kicker: roman + tag */}
      <div className="flex items-center gap-3 mb-6">
        <span className="font-serif italic text-gold/75 text-sm tracking-[0.12em]">
          {ROMANS[index] || index + 1}.
        </span>
        <span className="text-[10px] tracking-[0.45em] uppercase text-cotton/45 font-light">
          {stat.tag}
        </span>
        {live && (
          <span className="ml-1 inline-flex items-center gap-1.5">
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inset-0 rounded-full bg-red animate-ping opacity-70" />
              <span className="relative inline-flex rounded-full bg-red w-1.5 h-1.5" />
            </span>
            <span className="text-[9px] tracking-[0.4em] uppercase text-red font-light">Live</span>
          </span>
        )}
      </div>

      {/* The big number */}
      <p
        className="text-gold leading-[0.92] tracking-[-0.01em] font-condensed font-black"
        style={{
          fontSize: "clamp(56px, 6.5vw, 112px)",
          fontWeight: 900,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {displayed}
      </p>

      {/* Underline + label */}
      <div className="mt-5 flex items-center gap-3">
        <span className="block h-px w-8 bg-red transition-all duration-500 group-hover:w-14" />
        <p
          className="font-serif italic text-cotton/75 tracking-wide"
          style={{ fontSize: "clamp(15px, 1.1vw, 18px)" }}
        >
          {stat.label}
        </p>
      </div>
    </div>
  );
};

const Counter = () => {
  const { content } = useContent();
  const c = { ...FALLBACK, ...(content?.counter || {}) };
  const stats = c.stats?.length ? c.stats : FALLBACK.stats;

  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setInView(true);
          el.querySelectorAll(".reveal").forEach((n) => {
            if (e.isIntersecting) n.classList.add("in");
          });
        });
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid="counter"
      className="relative bg-black border-t border-cotton/10 py-28 md:py-36 px-6 overflow-hidden"
    >
      {/* Decorative top tag */}
      <div className="max-w-7xl mx-auto">
        <div className="reveal flex items-center gap-4 mb-2">
          <span className="block w-10 h-px bg-gold/60" />
          <span className="text-[10px] tracking-[0.5em] uppercase text-gold/80 font-light">
            {c.ledgerTag}
          </span>
        </div>
        <p className="reveal font-serif italic text-cotton/45 mb-16 md:mb-20 ml-14"
           style={{ fontSize: "clamp(13px, 1vw, 15px)" }}>
          By the numbers, kept honest.
        </p>

        <div className="reveal grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-12">
          {stats.map((s, i) => (
            <StatBlock key={i} stat={s} index={i} inView={inView} />
          ))}
        </div>

        <div className="reveal mt-24 flex items-center gap-6">
          <span className="block w-12 h-px bg-red" />
          <p
            data-testid="counter-footnote"
            className="text-cotton/55 font-condensed font-light italic tracking-wide text-base md:text-lg"
          >
            {c.footnote}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Counter;
